import React from "react";
import Badge from "../../../../components/Atoms/Badge";
import Button from "../../../../components/Atoms/Button";

/**
 * The deployment pack for one trained version.
 *
 * THE PACK IS PROVIDER-AGNOSTIC, and this card says nothing about who might run it.
 * That is not tidiness: the pack is ONE immutable artifact with ONE fingerprint, and
 * naming a provider on it would make a second target look like a second pack. Where it
 * may be SENT is the export-target list below, which is the provider-specific half.
 *
 * This is NOT another export format either. The three buttons beside it hand a file to
 * the person clicking; this publishes an IMMUTABLE, CHECKSUMMED artifact that a
 * customer's edge box can later be asked to run — so the card shows the fingerprint, the
 * input size and the exact classes, because those are the three things that decide
 * whether the model behaves the same over there as it did here.
 * See g_portal/docs/MODEL_BRIDGE_PLAN.md §5.
 */
const STATE = {
  ready: { variant: "success", label: "Published" },
  building: { variant: "info", label: "Building…" },
  failed: { variant: "danger", label: "Failed" },
};

const bytes = (n) => {
  if (typeof n !== "number" || n <= 0) return "-";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const DeploymentPackCard = ({
  modelType,
  pack,
  loading,
  publishing,
  error,
  onPublish,
  onDownload,
}) => {
  // Deployment packs are detection-only (decision 2, 2026-09-09) — and this gate is about
  // THE BUILDER, not about any target: `b_model_pack.claim()` raises for any other task,
  // so a Publish button here would have exactly one outcome, a 4xx. Which TARGET can run a
  // pack once it exists is a different question, answered per row by the descriptor's
  // acceptsTasks in ExportTargets.jsx.
  const isDetection = modelType === "detection" || modelType === "bounding_box";
  const status = pack?.status;
  const chip = STATE[status];

  return (
    <div className="export-section__card deploypack">
      <div className="export-section__card-header deploypack__header">
        <strong>Deployment pack</strong>
        {chip && <Badge variant={chip.variant}>{chip.label}</Badge>}
      </div>
      <div className="export-section__card-body">
        {!isDetection ? (
          <p className="deploypack__note">
            Deployment packs are available for <strong>detection</strong> models only — the
            pack builder has no export for {modelType || "this task"}.
          </p>
        ) : (
          <>
            {error && <div className="deploypack__alert" role="alert">{error}</div>}

            {loading && !pack && <p className="deploypack__note">Reading…</p>}

            {!loading && !pack && !error && (
              <p className="deploypack__note">
                Not published yet. Publishing builds a self-contained pack — the trained
                checkpoint, an OpenVINO build and a manifest — that can be sent to one of
                the targets below once a platform admin approves it.
              </p>
            )}

            {status === "building" && (
              <p className="deploypack__note">
                Building the OpenVINO model. This takes up to a minute; you can leave this
                page — the result is saved against the version.
              </p>
            )}

            {status === "failed" && (
              <div className="deploypack__alert" role="alert">
                {pack.error || "The build failed."}
              </div>
            )}

            {status === "ready" && (
              <dl className="deploypack__facts">
                <dt>Pack id</dt>
                <dd><code className="deploypack__token">{pack.packId}</code></dd>
                <dt>Fingerprint</dt>
                {/* The FULL digest is in the DOM and clipped by CSS. Sixteen characters
                    of a sha256 is not a fingerprint — this is the value someone compares
                    against the copy that reached the other side. */}
                <dd><code className="deploypack__token">{pack.packSha256}</code></dd>
                <dt>Input size</dt>
                <dd>{pack.imgsz ? `${pack.imgsz} × ${pack.imgsz}` : "-"}</dd>
                <dt>Size</dt>
                <dd>{bytes(pack.packBytes)}</dd>
                <dt>Classes</dt>
                <dd className="deploypack__classes">
                  {(pack.classNames || []).length
                    ? pack.classNames.map((c) => (
                        <Badge key={c} variant="neutral">{c}</Badge>
                      ))
                    : "-"}
                </dd>
              </dl>
            )}

            <div className="deploypack__actions">
              {status === "ready" ? (
                <>
                  <Button variant="primary" size="sm" onClick={onDownload}>
                    Download pack
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={publishing}
                    onClick={() => onPublish({ force: true })}
                    tooltipText="Rebuild from the current checkpoint"
                  >
                    {publishing ? "Rebuilding…" : "Rebuild"}
                  </Button>
                </>
              ) : (
                status !== "building" && (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={publishing || loading}
                    onClick={() => onPublish({})}
                  >
                    {publishing ? "Starting…" : status === "failed" ? "Retry" : "Publish for deployment"}
                  </Button>
                )
              )}
            </div>

            {status === "ready" && (
              <p className="export-section__hint deploypack__hint">
                The pack is stored against this version and is immutable. Retraining
                produces a new pack; this one keeps its id and fingerprint.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeploymentPackCard;
